/**
 * Kleine Hilfsfunktionen des Wissen-Moduls: Byte-Formatierung und ein minimaler,
 * sicherer Markdown-Renderer (kein externes Paket noetig).
 */

/** Formatiert eine Byte-Zahl menschenlesbar (KB/MB). */
export function formatiereGroesse(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Sortiert Ordner stabil nach Sortierung, dann Name. */
const ordnerSortiert = (liste) =>
  [...liste].sort((a, b) => (a.sortierung - b.sortierung) || a.name.localeCompare(b.name));

/**
 * Baut aus der flachen Ordnerliste einen Baum. Jeder Knoten erhaelt ein
 * `kinder`-Array. Wurzeln sind Ordner ohne (bekannten) Elternteil.
 */
export function baueOrdnerBaum(ordner) {
  const nachId = new Map(ordner.map((o) => [o.id, { ...o, kinder: [] }]));
  const wurzeln = [];
  for (const knoten of nachId.values()) {
    const eltern = knoten.parentId != null ? nachId.get(knoten.parentId) : null;
    if (eltern) eltern.kinder.push(knoten);
    else wurzeln.push(knoten);
  }
  const sortiere = (knoten) => {
    knoten.kinder = ordnerSortiert(knoten.kinder);
    knoten.kinder.forEach(sortiere);
  };
  const sortierteWurzeln = ordnerSortiert(wurzeln);
  sortierteWurzeln.forEach(sortiere);
  return sortierteWurzeln;
}

/**
 * Flache Liste in Baum-Reihenfolge, je Eintrag mit `tiefe` (0 = Wurzel).
 * Praktisch fuer eingerueckte Auswahl-Dropdowns.
 */
export function ordnerMitTiefe(ordner) {
  const ergebnis = [];
  const gehe = (knoten, tiefe) => {
    ergebnis.push({ ...knoten, tiefe });
    knoten.kinder.forEach((k) => gehe(k, tiefe + 1));
  };
  baueOrdnerBaum(ordner).forEach((w) => gehe(w, 0));
  return ergebnis;
}

const escapeHtml = (text) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/** Wendet die Inline-Regeln (Code, fett, kursiv, durchgestrichen, Link) an. */
function inlineMarkdown(text) {
  return text
    .replace(/`([^`]+)`/g, '<code class="rounded bg-paper-sunken px-1 py-0.5 text-sm">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/~~([^~]+)~~/g, '<del>$1</del>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-brand-600 underline">$1</a>');
}

/** Zerlegt eine Tabellenzeile in getrimmte Zellen. */
const tabellenZellen = (zeile) =>
  zeile.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((z) => z.trim());

/** Rendert einen Tabellenblock (Kopf + Zeilen) zu HTML. */
function rendereTabelle(kopf, zeilen) {
  const th = kopf.map((z) => `<th class="border border-line px-3 py-1.5 text-left font-semibold">${inlineMarkdown(z)}</th>`).join('');
  const tr = zeilen
    .map((zellen) => `<tr>${zellen.map((z) => `<td class="border border-line px-3 py-1.5">${inlineMarkdown(z)}</td>`).join('')}</tr>`)
    .join('');
  return `<div class="my-2 overflow-x-auto"><table class="w-full border-collapse text-sm"><thead class="bg-paper-sunken text-ink"><tr>${th}</tr></thead><tbody>${tr}</tbody></table></div>`;
}

/**
 * Rendert einen Markdown-Teilsatz (Ueberschriften, Listen, Checklisten, Zitate,
 * Codebloecke, Tabellen, Trennlinien, Inline-Formatierung) zu HTML. Eingabe wird
 * zuvor escaped -> kein HTML-Injection-Risiko. Blockbasiert (Index-Schleife mit
 * Vorausschau fuer Tabellen/Codebloecke).
 */
export function rendereMarkdown(quelle) {
  if (!quelle) return '';
  const zeilen = escapeHtml(quelle).split(/\r?\n/);
  const html = [];
  let liste = null; // { tag, klasse } | null

  const schliesseListe = () => {
    if (liste) {
      html.push(`</${liste.tag}>`);
      liste = null;
    }
  };
  const oeffneListe = (tag, klasse) => {
    if (liste && (liste.tag !== tag || liste.klasse !== klasse)) schliesseListe();
    if (!liste) {
      html.push(`<${tag} class="${klasse}">`);
      liste = { tag, klasse };
    }
  };

  let i = 0;
  while (i < zeilen.length) {
    const zeile = zeilen[i];

    // Codeblock (```-Fence): Inhalt roh (bereits escaped), keine Inline-Regeln.
    if (/^\s*```/.test(zeile)) {
      schliesseListe();
      const code = [];
      i++;
      while (i < zeilen.length && !/^\s*```/.test(zeilen[i])) code.push(zeilen[i++]);
      i++;
      html.push(`<pre class="my-2 overflow-x-auto rounded-lg bg-paper-sunken p-3 text-sm text-ink"><code>${code.join('\n')}</code></pre>`);
      continue;
    }

    // Tabelle: Kopfzeile mit | und folgende Trennzeile (---).
    const naechste = zeilen[i + 1];
    if (zeile.includes('|') && naechste && /^\s*\|?[\s:|-]*-[-\s:|]*\|?\s*$/.test(naechste)) {
      schliesseListe();
      const kopf = tabellenZellen(zeile);
      i += 2;
      const koerper = [];
      while (i < zeilen.length && zeilen[i].includes('|') && zeilen[i].trim() !== '') {
        koerper.push(tabellenZellen(zeilen[i++]));
      }
      html.push(rendereTabelle(kopf, koerper));
      continue;
    }

    // Zitat: aufeinanderfolgende >-Zeilen zusammenfassen.
    if (/^\s*>\s?/.test(zeile)) {
      schliesseListe();
      const zitat = [];
      while (i < zeilen.length && /^\s*>\s?/.test(zeilen[i])) {
        zitat.push(zeilen[i++].replace(/^\s*>\s?/, ''));
      }
      const inhalt = zitat.map((z) => `<p>${inlineMarkdown(z)}</p>`).join('');
      html.push(`<blockquote class="my-2 border-l-4 border-brand-300 bg-paper-soft py-1 pl-3 text-ink-soft">${inhalt}</blockquote>`);
      continue;
    }

    const ueberschrift = zeile.match(/^(#{1,3})\s+(.*)$/);
    const checkliste = zeile.match(/^\s*[-*]\s+\[([ xX])\]\s+(.*)$/);
    const nummeriert = zeile.match(/^\s*\d+\.\s+(.*)$/);
    const punkt = zeile.match(/^\s*[-*]\s+(.*)$/);

    if (/^\s*(-{3,}|\*{3,})\s*$/.test(zeile)) {
      schliesseListe();
      html.push('<hr class="my-3 border-line"/>');
    } else if (ueberschrift) {
      schliesseListe();
      const stufe = ueberschrift[1].length;
      const groesse = stufe === 1 ? 'text-xl' : stufe === 2 ? 'text-lg' : 'text-base';
      html.push(`<h${stufe} class="mt-3 mb-1 ${groesse} font-semibold text-ink">${inlineMarkdown(ueberschrift[2])}</h${stufe}>`);
    } else if (checkliste) {
      oeffneListe('ul', 'my-1 list-none space-y-1 pl-1');
      const angehakt = checkliste[1] !== ' ';
      html.push(
        `<li class="flex items-start gap-2"><input type="checkbox" disabled ${angehakt ? 'checked' : ''} class="mt-1 accent-brand-600"/><span>${inlineMarkdown(checkliste[2])}</span></li>`
      );
    } else if (nummeriert) {
      oeffneListe('ol', 'my-1 list-decimal pl-5');
      html.push(`<li>${inlineMarkdown(nummeriert[1])}</li>`);
    } else if (punkt) {
      oeffneListe('ul', 'my-1 list-disc pl-5');
      html.push(`<li>${inlineMarkdown(punkt[1])}</li>`);
    } else if (zeile.trim() === '') {
      schliesseListe();
    } else {
      schliesseListe();
      html.push(`<p class="my-1">${inlineMarkdown(zeile)}</p>`);
    }
    i++;
  }
  schliesseListe();
  return html.join('');
}

/**
 * Liefert den Ordnerpfad (Wurzel -> Zielordner) als Array fuer die Breadcrumb.
 * Robust gegen Zyklen. Leeres Array fuer die Wurzel (id == null).
 */
export function ordnerPfad(ordner, id) {
  const nachId = new Map(ordner.map((o) => [o.id, o]));
  const pfad = [];
  const gesehen = new Set();
  let aktuell = id != null ? nachId.get(id) : null;
  while (aktuell && !gesehen.has(aktuell.id)) {
    gesehen.add(aktuell.id);
    pfad.unshift(aktuell);
    aktuell = aktuell.parentId != null ? nachId.get(aktuell.parentId) : null;
  }
  return pfad;
}

/** Zaehlt direkte Unterordner und direkte Notizen eines Ordners. */
export function zaehleOrdnerInhalt(ordner, notizen, id) {
  return {
    unterordner: ordner.filter((o) => o.parentId === id).length,
    notizen: notizen.filter((n) => n.ordnerId === id).length,
  };
}
